import React, { useMemo, useState } from 'react'
import { StyleSheet, ViewProps } from 'react-native'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { Text, UploadFile, UploadFileError } from 'zens-storybook'
import { BuildingData, DataWithUpload, Draft, useBuilding } from 'zens-store'
import {
  Form,
  NavigationConfirm,
  FormCheckbox,
  FormProps,
  UploadPlaceholder,
  UploadDragging,
  AddressForm,
} from '@dms/component'

import { observer } from 'mobx-react'

import { Href } from 'zens-router'

const styles = StyleSheet.create({
  checkbox: {
    minHeight: 46,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  locationId: number
  buildingId?: number
  onSave?: (draft: Draft<DataWithUpload<BuildingData>>) => Promise<void>
  onDelete?: (draft: Draft<BuildingData>) => Promise<void>
  cancelHref?: Href
  progress?: number
}

const BuildingForm = (props: Props) => {
  const { companyId, locationId, buildingId = 0, onSave, onDelete, onError, cancelHref, ...otherProps } = props
  const building = useBuilding({ companyId, buildingId })
  const draft = useMemo(
    () =>
      new Draft<DataWithUpload<BuildingData>>({
        id: 0,
        company_id: companyId,
        location_id: locationId,
        ...building.data?.data,
      }),
    [building.isFetched],
  )
  const hasAddress =
    draft.data.address?.address_line_1 ||
    draft.data.address?.city ||
    draft.data.address?.zipcode ||
    draft.data.address?.country_code

  const [locationAddress, setLocationAddress] = useState(!hasAddress)
  const { t } = useTranslation()

  const updatePhoto = (value?: string, file?: File) => {
    draft.update('upload', file)
    draft.update('src', value || null)
    onError?.({})
  }

  const updateUploadError = (error: UploadFileError) => {
    switch (error) {
      case UploadFileError.FileInvalidType:
        onError?.({ src: t('Image type must be JPG, PNG or BMP') })
        break
      case UploadFileError.FileTooLarge:
        onError?.({ src: t('Image too large') })
        break
      case UploadFileError.FileTooSmall:
        onError?.({ src: t('Image too small') })
        break
      default:
        onError?.({ src: t('Image selection failed') })
    }
  }

  const save = async (draft: Draft<DataWithUpload<BuildingData>>) => {
    if (locationAddress) {
      draft.data.address = undefined
    }
    return onSave?.(draft)
  }

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'name',
            label: t('Building name'),
            help: t('Define the name of the building where you want to start adding workspaces and devices to.'),
            fieldProps: {
              testID: 'BuildingForm.Name',
            },
          },
          {
            key: 'src',
            label: t('Upload an image'),
            optional: true,
            help: t(
              'Add a visual impression of the building. This image will be visible on the homepage and on the dashboard.',
            ),
            FieldInputComponent: UploadFile,
            fieldProps: {
              placeholder: <UploadPlaceholder />,
              placeholderDragging: <UploadDragging />,
              deleteTitle: t('Delete image'),
              onValueChange: updatePhoto,
              onError: updateUploadError,
              progress: draft.data.progress,
            },
          },
        ],
      },
      {
        label: t('Address'),
        fields: [
          {
            key: 'company_address',
            FieldInputComponent: FormCheckbox,
            fieldProps: {
              label: t('Use location address for this building'),
              testID: 'BuidingForm.CompanyAddress',
              value: locationAddress,
              onChange: setLocationAddress,
            },
            style: styles.checkbox,
          },
        ],
      },
    ],
  }
  const schema = object({
    name: string().required(t('Building name is required')),
  })
  const buttons = [
    [
      {
        title: t('Save'),
        onPress: save,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: cancelHref,
        light: true,
      },
    ],
    ...(onDelete
      ? [
          {
            title: t('Delete building'),
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
          },
        ]
      : []),
  ]
  return (
    <>
      {locationAddress ? (
        <Form draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      ) : (
        <AddressForm draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      )}
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {
  numberOfbuttons?: number | number[]
}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        fields: [
          {
            key: 'name',
            label: t('Building name'),
          },
          {
            key: 'src',
            label: t('Upload an image'),
            FieldInputComponent: UploadFile.Skeleton,
          },
        ],
      },
      {
        label: t('Address'),
        fields: [
          {
            key: 'company_address',
            FieldInputComponent: Text.Skeleton,
            style: styles.checkbox,
          },
          {
            key: 'location_id',
            label: t('Address'),
            FieldInputComponent: Text.Skeleton,
          },
        ],
      },
    ],
  }

  return <Form.Skeleton style={style} config={config} {...otherProps} />
}
BuildingForm.Skeleton = Skeleton

export default observer(BuildingForm)
